import { useEffect, useState } from "react";
import { http, formatErr } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Plus, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", slug: "", content: "", meta_description: "", is_published: true, show_in_nav: true, nav_order: 0 };

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // null | empty | page

  const load = () => http.get("/admin/pages").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await http.put(`/admin/pages/${editing.id}`, editing);
      else await http.post(`/admin/pages`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    await http.delete(`/admin/pages/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Pages</h1>
          <p className="text-white/45 text-sm mt-1">Custom CMS pages (About, FAQ, Policies…)</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="bls-admin-btn" data-testid={TID.adminPageNew}>
          <Plus className="w-4 h-4" /> New page
        </button>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bls-admin-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className="bls-admin-th">Title</th>
                <th className="bls-admin-th">Slug</th>
                <th className="bls-admin-th">In nav</th>
                <th className="bls-admin-th">Status</th>
                <th className="bls-admin-th text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 && <tr><td colSpan={5} className="bls-admin-td text-center text-white/45">No pages</td></tr>}
              {pages.map((p) => (
                <tr key={p.id} className="bls-admin-tr" data-testid={`${TID.adminPageRow}-${p.slug}`}>
                  <td className="bls-admin-td font-medium">{p.title}</td>
                  <td className="bls-admin-td text-xs text-white/55 font-mono">/p/{p.slug}</td>
                  <td className="bls-admin-td">{p.show_in_nav ? "Yes" : "No"}</td>
                  <td className="bls-admin-td">
                    <span className={`bls-status ${p.is_published ? "bls-status-paid" : "bls-status-failed"}`}>
                      {p.is_published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="bls-admin-td text-right">
                    <button onClick={() => setEditing(p)} className="bls-admin-btn-ghost mr-2"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => remove(p.id)} className="bls-admin-btn-ghost"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bls-admin-card p-6">
          {!editing && <div className="text-white/45 text-sm text-center py-8">Select a page to edit or create a new one.</div>}
          {editing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold">{editing.id ? "Edit page" : "New page"}</h2>
                <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <F label="Title" v={editing.title} on={(x) => setEditing({ ...editing, title: x })} t="admin-page-title" />
              <F label="Slug" v={editing.slug} on={(x) => setEditing({ ...editing, slug: x })} t="admin-page-slug" placeholder="auto" />
              <F label="Meta description" v={editing.meta_description} on={(x) => setEditing({ ...editing, meta_description: x })} t="admin-page-meta" />
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 font-medium">Content (HTML)</label>
                <textarea
                  value={editing.content}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  className="bls-admin-input mt-2 h-48 font-mono text-xs"
                  data-testid="admin-page-content"
                />
              </div>
              <F label="Nav order" v={editing.nav_order} on={(x) => setEditing({ ...editing, nav_order: Number(x) || 0 })} type="number" t="admin-page-order" />
              <T label="Show in navigation" v={editing.show_in_nav} on={(x) => setEditing({ ...editing, show_in_nav: x })} t="admin-page-nav" />
              <T label="Published" v={editing.is_published} on={(x) => setEditing({ ...editing, is_published: x })} t="admin-page-pub" />
              <button onClick={save} className="bls-admin-btn w-full" data-testid={TID.adminPageSave}>
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, t, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50 font-medium">{label}</label>
      <input type={type} value={v} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="bls-admin-input mt-2" data-testid={t} />
    </div>
  );
}
function T({ label, v, on, t }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-white/80">{label}</span>
      <button type="button" onClick={() => on(!v)} data-testid={t} className={`relative w-10 h-5 rounded-full transition-colors ${v ? "bg-[#7f77dd]" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${v ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}
